import type { SalonService } from "../../../../types/salon";
import type { ServiceRow } from "./bookingTypes";
import { isStaffQualified } from "./guards";

/** Day-view columns blocked for the active row's service (prototype `noDropFor`). */
export interface BlockedStaff {
  serviceName: string;
  /** Staff (shown columns) who don't offer it. */
  staffIds: ReadonlySet<string>;
}

/**
 * Staff among `shownStaffIds` who can't perform the active row's service, or
 * null when the row has no service yet (nothing to block). The
 * staff ↔ service source is `servicing_staff` (isStaffQualified).
 */
export function blockedStaffFor(
  row: Pick<ServiceRow, "salonServiceId"> | undefined,
  services: SalonService[],
  shownStaffIds: string[],
): BlockedStaff | null {
  if (!row?.salonServiceId) return null;
  const svc = services.find((s) => s.id === row.salonServiceId);
  if (!svc) return null;
  const staffIds = new Set(
    shownStaffIds.filter((id) => !isStaffQualified(svc, id)),
  );
  return staffIds.size ? { serviceName: svc.name, staffIds } : null;
}

/** Error toast copy (prototype): a click on a blocked column. */
export const blockedClickToast = (serviceName: string, who: string) => ({
  title: `Can’t book ${serviceName} there`,
  note: `${who} doesn’t offer this service. Pick a highlighted column.`,
});

/** Error toast copy (prototype): a drop on a blocked column. */
export const blockedDropToast = (serviceName: string, who: string) => ({
  title: `Can’t move ${serviceName} there`,
  note: `${who} doesn’t offer this service.`,
});

/** Column label over a blocked column. */
export const blockedColumnLabel = (serviceName: string) =>
  `Doesn’t offer ${serviceName}`;
